import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./FloatingSupportChat.module.css";

const resolveLangFromPath = (pathname: string): string | null => {
  const segments = pathname.split("/").filter((segment) => segment.length > 0);
  if (segments.length === 0) {
    return null;
  }
  return segments[0];
};

const FloatingSupportChat: React.FC = () => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const targetLang = useMemo(() => {
    const routeLang = resolveLangFromPath(location.pathname);
    if (routeLang && routeLang.length > 0) {
      return routeLang;
    }
    return i18n.language || "en";
  }, [i18n.language, location.pathname]);

  const isChatPage = location.pathname.includes("/support/chatbot");
  if (isChatPage) {
    return null;
  }

  const onConfirm = () => {
    setIsDialogOpen(false);
    navigate(`/${targetLang}/support/chatbot`);
  };

  return (
    <>
      <button
        type="button"
        className={styles.floatingButton}
        onClick={() => setIsDialogOpen(true)}
        aria-label={t("floating_support_chat.open", { defaultValue: "Open AI chat" })}
      >
        <span className={styles.icon}>💬</span>
        <span className={styles.label}>
          {t("floating_support_chat.label", { defaultValue: "Start AI Support" })}
        </span>
      </button>

      {isDialogOpen && (
        <div
          className={styles.backdrop}
          role="dialog"
          aria-modal="true"
          aria-labelledby="floating-support-chat-title"
        >
          <div className={styles.dialog}>
            <h2 id="floating-support-chat-title" className={styles.title}>
              {t("floating_support_chat.confirm_title", {
                defaultValue: "Do you want to go to the AI support page?",
              })}
            </h2>
            <p className={styles.description}>
              {t("floating_support_chat.confirm_description", {
                defaultValue:
                  "Press Yes to move to the AI support page.",
              })}
            </p>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => setIsDialogOpen(false)}
              >
                {t("floating_support_chat.no", { defaultValue: "No" })}
              </button>
              <button
                type="button"
                className={styles.confirmButton}
                onClick={onConfirm}
              >
                {t("floating_support_chat.yes", { defaultValue: "Yes" })}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingSupportChat;
